/**
 * Onboarding Tour - Guided tour for first-time users
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  XMarkIcon,
  UserGroupIcon,
  WifiIcon,
  BellIcon,
  RocketLaunchIcon,
} from '@heroicons/react/24/outline';

interface OnboardingStep {
  title: string;
  description: string;
  icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  actionText: string;
  actionPath?: string;
}

const ONBOARDING_STEPS: OnboardingStep[] = [
  {
    title: 'Welcome to Fawayo!',
    description: 'Share your VPN connection with friends securely and easily. Let\'s get you started with a quick tour.',
    icon: RocketLaunchIcon,
    actionText: 'Start Tour',
  },
  {
    title: 'Add Friends',
    description: 'Share your friend code or use someone else\'s code to connect. Once friends, you can share VPN access with each other.',
    icon: UserGroupIcon,
    actionText: 'Go to Friends',
    actionPath: '/friends',
  },
  {
    title: 'Manage Connections',
    description: 'Start hosting your VPN or connect to a friend\'s VPN. You\'ll see real-time connection quality and bandwidth usage.',
    icon: WifiIcon,
    actionText: 'Go to Connections',
    actionPath: '/connections',
  },
  {
    title: 'Stay Updated',
    description: 'Get instant notifications when friends send requests or when connections are established. Check the bell icon in the header!',
    icon: BellIcon,
    actionText: 'Got it!',
  },
];

const OnboardingTour: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  const getStorageKey = useCallback(() => {
    return user?.id ? `hasSeenOnboarding_${user.id}` : 'hasSeenOnboarding';
  }, [user?.id]);

  useEffect(() => {
    // Check if user has seen the onboarding
    const hasSeenOnboarding = localStorage.getItem(getStorageKey());
    if (!hasSeenOnboarding && user?.id) {
      // Show onboarding after a brief delay (only for authenticated users)
      setTimeout(() => setIsOpen(true), 500);
    }
  }, [getStorageKey, user?.id]);

  const handleClose = () => {
    setIsOpen(false);
    if (user?.id) {
      localStorage.setItem(getStorageKey(), 'true');
    }
  };

  const handleNext = () => {
    const step = ONBOARDING_STEPS[currentStep];

    if (currentStep === ONBOARDING_STEPS.length - 1) {
      // Last step - close
      handleClose();
    } else if (step.actionPath && currentStep > 0) {
      // Navigate and close for middle steps
      handleClose();
      navigate(step.actionPath);
    } else {
      // Move to next step
      setCurrentStep(prev => prev + 1);
    }
  };

  const handleSkip = () => {
    handleClose();
  };

  if (!isOpen) return null;

  const step = ONBOARDING_STEPS[currentStep];
  const Icon = step.icon;
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === ONBOARDING_STEPS.length - 1;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black bg-opacity-50 z-[50]" />

      {/* Modal */}
      <div className="fixed inset-0 z-[51] flex items-center justify-center p-4">
        <div className="bg-white rounded-lg shadow-xl max-w-md w-full relative">
          {/* Close button */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>

          {/* Content */}
          <div className="p-6">
            {/* Icon */}
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-blue-100 rounded-full">
                <Icon className="h-12 w-12 text-primary-600" />
              </div>
            </div>

            {/* Title */}
            <h2 className="text-2xl font-bold text-gray-900 text-center mb-3">
              {step.title}
            </h2>

            {/* Description */}
            <p className="text-gray-600 text-center mb-6">
              {step.description}
            </p>

            {/* Progress dots */}
            <div className="flex justify-center space-x-2 mb-6">
              {ONBOARDING_STEPS.map((_, index) => (
                <div
                  key={index}
                  className={`h-2 w-2 rounded-full transition-colors ${
                    index === currentStep
                      ? 'bg-primary-600'
                      : index < currentStep
                      ? 'bg-blue-300'
                      : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>

            {/* Actions */}
            <div className="flex space-x-3">
              {!isFirstStep && !isLastStep && (
                <button
                  onClick={handleSkip}
                  className="flex-1 px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 transition-colors duration-200 transition-colors"
                >
                  Skip Tour
                </button>
              )}
              <button
                onClick={handleNext}
                className="flex-1 px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 transition-colors duration-200 transition-colors"
              >
                {step.actionText}
              </button>
            </div>

            {/* Step counter */}
            <div className="text-center mt-4 text-xs text-gray-500">
              Step {currentStep + 1} of {ONBOARDING_STEPS.length}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default OnboardingTour;
